import { Capability } from './capabilities';
import { RoleNames, isSameRoleName } from './roleNames';
import { BriqueNames, RoleSession } from './sessionContext';
import { ALL_ROLES } from './visibilityPolicy';

const { Admin, Courtier, Mia, Pim, GestionnaireLead, Plateforme } = RoleNames;

/** Deviseurs : Courtier + mandataires MIA/PIM (mêmes droits de souscription,
 * SAUF les contrats de groupe — ils utilisent ceux de leurs courtiers mandants). */
const Deviseurs = [Courtier, Mia, Pim] as const;

/**
 * MATRICE capacité → rôles autorisés. LA source de vérité des droits par rôle actif.
 * Un rôle non listé = interdit. Changer un droit = changer une ligne ici.
 */
const CAPABILITY_ROLES: Record<Capability, readonly string[] | typeof ALL_ROLES> = {
  [Capability.SimulationOpen]: Deviseurs,
  [Capability.SimulationActions]: Deviseurs,

  [Capability.QuoteOpen]: Deviseurs,
  [Capability.QuoteActions]: Deviseurs,

  [Capability.LeadAssign]: [GestionnaireLead],

  [Capability.ClientDetailActions]: Deviseurs,

  [Capability.ContractOpen]: ALL_ROLES,
  [Capability.ContractViewActions]: ALL_ROLES,
  [Capability.ContractWriteActions]: Deviseurs,
  [Capability.ContractDetailActions]: Deviseurs,

  [Capability.GroupContractActions]: [Courtier, Plateforme],
  [Capability.GroupContractBrokerMode]: [Courtier],
  [Capability.GroupContractManageBrokers]: [Admin, Plateforme],

  [Capability.ActivityFeedLinks]: Deviseurs,
  [Capability.ListNotificationBadges]: Deviseurs,

  [Capability.NotesAccess]: Deviseurs,

  [Capability.SubscriptionProcessAccess]: Deviseurs,

  [Capability.CreateMenuAccess]: Deviseurs,

  [Capability.PlateformePortfolioLists]: [Plateforme],

  // Legacy : comportement historique inchangé (l'accès reste borné par
  // compagnieOnly côté menu et par les gardes serveur) — la restriction
  // Conception d'offre ne s'applique qu'aux sessions migrées aux briques.
  [Capability.TunnelHabilitationsAccess]: ALL_ROLES,

  // Aucun rôle legacy : les validations n'existent que sous le Portail Gestion
  // (brique Valideur) — liste vide = refus pour tout rôle.
  [Capability.ValidationsAccess]: [],

  // Legacy: historical behaviour, every role sees the search and the statistics.
  [Capability.GlobalSearchAccess]: ALL_ROLES,
  [Capability.PortfolioOverview]: ALL_ROLES
};

/** Le rôle possède-t-il la capacité ? (insensible à la casse ; rôle vide = non) */
export const roleCan = (
  roleName: string | null | undefined,
  capability: Capability
): boolean => {
  const allowed = CAPABILITY_ROLES[capability];
  if (allowed === ALL_ROLES) return true;
  if (!roleName) return false;
  return allowed.some((role) => isSameRoleName(role, roleName));
};

const {
  Distribution,
  Encaissement,
  SouscriptionCollective,
  ConsultationPortefeuilleOrga,
  ConsultationPorteurRisque,
  ConceptionOffre,
  GestionLeads,
  SouscriptionPlateforme,
  Valideur
} = BriqueNames;

/** Bricks that work on a portfolio (all of them except Valideur, the only
 * Portail Gestion brick): global search and /bord statistics. */
const PortefeuilleBriques = [
  Distribution,
  Encaissement,
  SouscriptionCollective,
  ConsultationPortefeuilleOrga,
  ConsultationPorteurRisque,
  ConceptionOffre,
  GestionLeads,
  SouscriptionPlateforme
] as const;

/**
 * MATRICE capacité → briques requises (mode portail). Le pendant briques de
 * CAPABILITY_ROLES : une capacité est accordée si la session porte AU MOINS UNE
 * des briques listées. Les briques sont des noms de capacités côté back — la
 * matrice est donc surtout un mapping UI ↔ brique, pas une politique nouvelle.
 */
const CAPABILITY_BRIQUES: Record<Capability, readonly string[] | typeof ALL_ROLES> = {
  [Capability.SimulationOpen]: [Distribution],
  [Capability.SimulationActions]: [Distribution],

  [Capability.QuoteOpen]: [Distribution],
  [Capability.QuoteActions]: [Distribution],

  [Capability.LeadAssign]: [GestionLeads],

  [Capability.ClientDetailActions]: [Distribution],

  [Capability.ContractOpen]: ALL_ROLES,
  [Capability.ContractViewActions]: ALL_ROLES,
  [Capability.ContractWriteActions]: [Distribution],
  [Capability.ContractDetailActions]: [Distribution],

  // Création/gestion des collectifs : côté Distribution la brique Souscription
  // collective (portée cabinet), côté Plateforme la brique Souscription
  // plateforme (portée plateforme, miroir de PeutCreerContratPlateformeAsync).
  // La Consultation portefeuille orga est en LECTURE seule — elle voit la liste
  // (ContratsDeGroupes.Read) mais ne porte aucune action.
  [Capability.GroupContractActions]: [SouscriptionCollective, SouscriptionPlateforme],
  [Capability.GroupContractBrokerMode]: [SouscriptionCollective],
  // Admin reste en mode legacy (matrice rôles). Côté portail, éditer les
  // courtiers d'un contrat de groupe appelle add/removeBrokerToGroupContract,
  // gardés back par ContratsDeGroupes.Manage — porté par la brique Souscription
  // plateforme (la Consultation est en lecture seule et prendrait un 403).
  [Capability.GroupContractManageBrokers]: [SouscriptionPlateforme],

  [Capability.ActivityFeedLinks]: [Distribution],
  [Capability.ListNotificationBadges]: [Distribution],

  [Capability.NotesAccess]: [Distribution],

  [Capability.SubscriptionProcessAccess]: [Distribution],

  [Capability.CreateMenuAccess]: [Distribution],

  // Le porteur de risque voit les mêmes listes : le serveur les borne à ses
  // tunnels portés (cross-plateformes) au lieu du portefeuille de l'orga.
  [Capability.PlateformePortfolioLists]: [ConsultationPortefeuilleOrga, ConsultationPorteurRisque],

  // Habilitations tunnels : geste de conception d'offre (le back porte le grant
  // Tunnels.ManageCourtierAccess sur cette brique aussi).
  [Capability.TunnelHabilitationsAccess]: [ConceptionOffre],

  // Validations de devis : seule brique du Portail Gestion.
  [Capability.ValidationsAccess]: [Valideur],

  // Every brick except Valideur: the Portail Gestion carries neither the
  // global search nor the portfolio statistics (its scope is the validation
  // requests addressed to the account).
  [Capability.GlobalSearchAccess]: PortefeuilleBriques,
  [Capability.PortfolioOverview]: PortefeuilleBriques
};

/**
 * La capacité est-elle accordée à la SESSION ? Mode legacy → matrice rôles ;
 * mode portail → matrice briques (briques effectives du portail actif).
 */
export const sessionCan = (
  session: RoleSession,
  capability: Capability
): boolean => {
  if (session.mode === 'legacy') {
    return roleCan(session.roleName, capability);
  }

  const requises = CAPABILITY_BRIQUES[capability];
  if (requises === ALL_ROLES) return true;
  return requises.some((brique) => session.briques.includes(brique));
};
